import {
  QuestionRouter_QuestionFragment,
  SurveyQuestionType,
  SurveySubmitAnswerInput,
} from '@/graphql/generated';
import { QuestionText } from './QuestionText';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { QuestionStatement } from './QuestionStatement';
import { QuestionMultipleChoice } from './QuestionMultipleChoice';
import { QuestionEnps } from './QuestionEnps';
import { QuestionIdentity } from './QuestionIdentity';
import { QuestionRating } from './QuestionRating';
import { QuestionStakesSimpleMateriality } from './QuestionStakesSimpleMateriality';
import { QuestionStakesRanking } from './QuestionStakesRanking';
import { QuestionStakesDoubleMateriality } from './QuestionStakesDoubleMateriality';
import { QuestionNumber } from './QuestionNumber';
import { QuestionUtopiesStakesDoubleMateriality } from './custom/QuestionUtopiesStakesDoubleMateriality';
import { QuestionIroAssessment } from './iroAssessment/QuestionIroAssessment';
import { useTranslation } from '@/hooks/useTranslation';

export function QuestionRouter({
  question,
  answer,
  setAnswer,
  setIsAnswerValid,
}: {
  question: QuestionRouter_QuestionFragment;
  answer: SurveySubmitAnswerInput | undefined;
  setAnswer: (answer: SurveySubmitAnswerInput | null) => void;
  setIsAnswerValid: (isValid: boolean) => void;
}) {
  const { t } = useTranslation();
  switch (question.type) {
    case SurveyQuestionType.Email:
      return (
        <QuestionIdentity
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.ShortText:
      return (
        <QuestionText
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.Number:
      return (
        <QuestionNumber
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.MultipleChoice:
    case SurveyQuestionType.MaturityEvaluation:
      return (
        <QuestionMultipleChoice
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.Enps:
      return (
        <QuestionEnps
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.Matrix:
      return (
        <QuestionStakesSimpleMateriality
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.StakeDoubleMateriality:
      return (
        <QuestionStakesDoubleMateriality
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.UtopiesStakeDoubleMateriality:
      return (
        <QuestionUtopiesStakesDoubleMateriality
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.StakesRanking:
      return (
        <QuestionStakesRanking
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.Rating:
      return (
        <QuestionRating
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.Statement:
      return <QuestionStatement question={question} />;
    case SurveyQuestionType.IroAssessment:
      return (
        <QuestionIroAssessment
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          setIsAnswerValid={setIsAnswerValid}
        />
      );
    case SurveyQuestionType.Group:
      return null;
    default:
      return (
        <div className="p-16">
          <MessageBox type={MessageBoxType.Warning}>
            {t('global:error')}
          </MessageBox>
        </div>
      );
  }
}
